/**
 * Palm Rejection Utility Functions
 * 
 * Centralized collection of palm rejection logic for:
 * - Input method detection (stylus, finger, mouse)
 * - Touch area calculation and threshold management
 * - Multi-factor palm detection analysis
 * - Active touch tracking and management
 * - Primary touch selection for multi-touch scenarios
 */

/**
 * Detect input method from event
 * @param {Event} event - Touch or pointer event
 * @param {Object} palmRejectionSettings - Palm rejection settings {inputMode}
 * @returns {string} Input method: 'stylus', 'finger', or 'mouse'
 */
export const detectInputMethod = (event, palmRejectionSettings) => {
    // Safety check: provide defaults if settings are undefined
    const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium' };
    
    // 1. Always trust explicit user settings (non-auto mode)
    if (settings.inputMode !== 'auto') {
        return settings.inputMode === 'stylus' ? 'stylus' : 'finger';
    }
    
    // 2. Use pointerType if available (most reliable)
    if (event.pointerType) {
        switch (event.pointerType) {
            case 'pen':
                return 'stylus';
            case 'touch':
                return 'finger';
            case 'mouse':
                return 'mouse';
            default:
                return 'finger'; // Default fallback
        }
    }
    
    // 3. For touch events without pointerType, use simple area-based detection
    if (event.touches) {
        const touch = event.touches[0] || event.changedTouches[0];
        if (touch && touch.radiusX !== undefined && touch.radiusY !== undefined) {
            const touchArea = touch.radiusX * touch.radiusY;
            // Simple threshold: small area = stylus, large area = finger
            return touchArea < 25 ? 'stylus' : 'finger';
        }
        // Fallback for touch events without radius data
        return 'finger';
    }
    
    // 4. Mouse events
    return 'mouse';
};

/**
 * Calculate touch contact area
 * @param {Touch} touch - Touch object from touch event
 * @returns {number} Touch area in square pixels
 */
export const calculateTouchArea = (touch) => {
    if (!touch) return 0;
    
    // Use radiusX and radiusY if available
    if (touch.radiusX !== undefined && touch.radiusY !== undefined && 
        touch.radiusX > 0 && touch.radiusY > 0) {
        return touch.radiusX * touch.radiusY; // Simple area calculation without complex normalization
    }
    
    // Fallback: estimate based on force/pressure when radius data unavailable
    const force = touch.force || touch.pressure || 0.5;
    return 100 + (force * 200); // 100-300 sq pixels range
};

/**
 * Calculate area threshold based on sensitivity setting
 * @param {string|number} sensitivity - Sensitivity level ('low', 'medium', 'high', 'very-high' or 1-10)
 * @returns {number} Area threshold in square pixels
 */
export const getAreaThreshold = (sensitivity) => {
    // Convert sensitivity to numeric value (1-10 scale)
    let numericSensitivity;
    if (typeof sensitivity === 'string') {
        switch (sensitivity.toLowerCase()) {
            case 'low': numericSensitivity = 3; break;
            case 'medium': numericSensitivity = 5; break;
            case 'high': numericSensitivity = 7; break;
            case 'very-high': numericSensitivity = 9; break;
            default: numericSensitivity = 5;
        }
    } else {
        numericSensitivity = Math.max(1, Math.min(10, sensitivity || 5));
    }
    
    // Simple threshold calculation: higher sensitivity = lower threshold
    // Range: 200 (very sensitive) to 800 (less sensitive)
    const threshold = 1000 - (numericSensitivity * 80);
    return Math.max(200, threshold);
};

/**
 * Enhanced multi-factor palm detection
 * @param {Touch} touch - Touch object to analyze
 * @param {string} inputMethod - Detected input method
 * @param {Object} palmRejectionSettings - Palm rejection settings
 * @param {Map} activeTouches - Map of currently active touches
 * @param {Object} palmDetector - MultiFactorPalmDetector instance
 * @param {Object} drawingState - Current drawing state {isDrawing, primaryDrawingTouch}
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @param {Function} trackRejection - Callback to track rejection analytics
 * @returns {boolean} True if touch should be rejected as palm
 */
export const shouldRejectTouchOptimized = (
    touch, 
    inputMethod, 
    palmRejectionSettings, 
    activeTouches, 
    palmDetector,
    drawingState,
    canvasSize,
    trackRejection
) => {
    // Safety check: provide defaults if settings are undefined
    const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium', multiTouchFiltering: true };
    
    if (!settings.enabled) return false;
    
    // Rule 0: Mode-based rejection (preserve existing logic)
    if (settings.inputMode === 'stylus' && inputMethod !== 'stylus') {
        return true;
    }
    
    if (settings.inputMode === 'finger' && inputMethod !== 'finger') {
        return true;
    }
    
    // Rule 1: Never reject stylus input (high confidence override)
    if (inputMethod === 'stylus') {
        return false;
    }
    
    // Rule 2: Use MultiFactorPalmDetector for enhanced analysis
    if (palmDetector) {
        try {
            // Build context for analysis
            const context = {
                isDrawing: drawingState.isDrawing,
                activeTouchCount: activeTouches ? activeTouches.size : 1,
                primaryTouchId: drawingState.primaryDrawingTouch,
                hasStylusActive: Array.from(activeTouches?.values() || []).some(t => t.inputMethod === 'stylus'),
                canvasWidth: canvasSize.width,
                canvasHeight: canvasSize.height
            };
            
            // Run multi-factor analysis
            const analysis = palmDetector.analyzeTouch(touch, context);
            
            // Decision threshold based on sensitivity
            const getThreshold = () => {
                switch (settings.sensitivity) {
                    case 'low': return 0.85;      // Very conservative - only reject obvious palms
                    case 'medium': return 0.70;   // Balanced - good for most users
                    case 'high': return 0.55;     // Aggressive - reject uncertain touches
                    case 'very-high': return 0.40; // Very aggressive - for stylus-heavy users
                    default: return 0.70;
                }
            };
            
            const threshold = getThreshold();
            const shouldReject = analysis.confidence > threshold;
            
            if (shouldReject && trackRejection) {
                // Track rejection for analytics
                trackRejection({
                    timestamp: Date.now(),
                    confidence: analysis.confidence,
                    threshold: threshold,
                    reasoning: analysis.reasoning[0] || 'Multi-factor analysis'
                });
            }
            
            return shouldReject;
            
        } catch (error) {
            console.error('Error in MultiFactorPalmDetector:', error);
            // Fallback to simplified detection on error
        }
    }
    
    // Fallback: Simplified detection if MultiFactorPalmDetector fails
    const touchArea = calculateTouchArea(touch);
    const areaThreshold = getAreaThreshold(settings.sensitivity);
    
    if (touchArea > areaThreshold) {
        return true;
    }
    
    // Legacy multi-touch filtering (preserved as backup)
    if (settings.multiTouchFiltering && activeTouches && activeTouches.size > 0) {
        const primaryTouch = getPrimaryTouch(activeTouches);
        
        if (primaryTouch) {
            const touchId = touch.identifier || 'pointer';
            
            if (touchId !== primaryTouch.id) {
                // Always reject non-stylus touches when stylus is active
                if (primaryTouch.inputMethod === 'stylus' && inputMethod !== 'stylus') {
                    return true;
                }
                
                // Reject touches significantly larger than primary
                if (touchArea > primaryTouch.area * 2.0) {
                    return true;
                }
            }
        }
    }
    
    return false;
};

/**
 * Update active touches tracking
 * @param {Event} event - Touch event
 * @param {string} eventType - Event type: 'start', 'move', or 'end'
 * @param {Map} activeTouches - Current active touches map
 * @param {Object} palmRejectionSettings - Palm rejection settings
 * @returns {Map} Updated active touches map
 */
export const updateActiveTouches = (event, eventType, activeTouches, palmRejectionSettings) => {
    // Safety check: provide defaults if settings are undefined
    const settings = palmRejectionSettings || { inputMode: 'auto', enabled: false, sensitivity: 'medium' };
    
    const newActiveTouches = new Map(activeTouches);
    const currentTimestamp = Date.now();
    
    if (eventType === 'start') {
        // Add new touches
        if (event.touches) {
            Array.from(event.touches).forEach(touch => {
                const touchData = {
                    id: touch.identifier,
                    startTime: currentTimestamp,
                    startX: touch.clientX,
                    startY: touch.clientY,
                    currentX: touch.clientX,
                    currentY: touch.clientY,
                    area: calculateTouchArea(touch),
                    pressure: touch.force || 0,
                    inputMethod: detectInputMethod(event, settings)
                };
                
                newActiveTouches.set(touch.identifier, touchData);
            });
        }
    } else if (eventType === 'move') {
        // Update existing touches
        if (event.touches) {
            Array.from(event.touches).forEach(touch => {
                if (newActiveTouches.has(touch.identifier)) {
                    const existing = newActiveTouches.get(touch.identifier);
                    const updatedTouch = {
                        ...existing,
                        currentX: touch.clientX,
                        currentY: touch.clientY,
                        area: calculateTouchArea(touch),
                        pressure: touch.force || 0
                    };
                    
                    newActiveTouches.set(touch.identifier, updatedTouch);
                }
            });
        }
    } else if (eventType === 'end') {
        // Remove ended touches
        if (event.changedTouches) {
            Array.from(event.changedTouches).forEach(touch => {
                newActiveTouches.delete(touch.identifier);
            });
        }
    }
    
    return newActiveTouches;
};

/**
 * Select primary touch from multiple active touches
 * @param {Map} touches - Map of active touches
 * @returns {Object|null} Primary touch data or null if no touches
 */
export const getPrimaryTouch = (touches) => {
    if (!touches || touches.size === 0) return null;
    
    const touchArray = Array.from(touches.values());
    
    // Simple, consistent rule: stylus first, then smallest area, then first chronologically
    let primaryTouch = touchArray[0];
    
    for (const touch of touchArray) {
        // Rule 1: Always prefer stylus over non-stylus
        if (touch.inputMethod === 'stylus' && primaryTouch.inputMethod !== 'stylus') {
            primaryTouch = touch;
            continue;
        }
        
        // Rule 2: Among same input types, prefer smaller area (more precise)
        if (touch.inputMethod === primaryTouch.inputMethod && touch.area < primaryTouch.area) {
            primaryTouch = touch;
            continue;
        }
        
        // Rule 3: If equal, prefer earlier touch (first touch wins)
        if (touch.inputMethod === primaryTouch.inputMethod && 
            touch.area === primaryTouch.area && 
            touch.startTime < primaryTouch.startTime) {
            primaryTouch = touch;
        }
    }
    
    return primaryTouch;
};
